import type { TPlayerBase } from 'video.js'
import type { DolbyPlugin } from '|>/core/addons/dolby'
import type { AdState } from '|>/core/addons/smartlib'
import type { PlayPauseAnimation } from '../play-pause-animation'
import type { ArrowSeekHandlers } from '../progress-control'

import videojs from 'video.js'
import { isDash, isHls } from '|>/core/helpers'
import { setCacheLastVolume } from '|>/shared/constants/volume'
import { isTouchDevice } from '|>/shared/dom'
import { Events } from '|>/shared/events'
import { silencePromise } from '|>/shared/helpers/promise'
import { Intent } from '|>/shared/types/intent'
import { on, register } from '|>/shared/vjs'
import { ArrowSeekAnimation } from '../arrow-seek-animation'
import {
  BaseButton,
  BaseMediaLoader,
  BasePlayer,
  BasePosterImage,
  BaseResizeManager,
  BaseTextTrackDisplay,
} from '../base'
import { ChildrenSlot } from '../children-slot'
import { ChromeCastingMessage } from '../chrome-casting-message'
import { CurrentTimeUpdater } from '../current-time-updater'
import { DebugOverlay } from '../debug-overlay'
import { ErrorDisplay } from '../error-display'
import { Hotkeys } from '../hotkeys'
import { LiveTracker } from '../live-tracker'
import { LoadingSpinner } from '../loading-spinner'
import { Poster } from '../poster'
import { TapHandler } from '../tap-handler'
import { TapSeekAnimation } from '../tap-seek-animation'
import { VolumePanel } from '../volume-panel'

/**
 * Main player component
 */
@register
export class Player extends BasePlayer {
  declare cachedSource: Events.Media.Load | undefined
  declare arrowSeekHandlers: ArrowSeekHandlers | undefined
  declare playPauseAnimation: PlayPauseAnimation | undefined
  declare dolbyPlugin: DolbyPlugin | undefined
  declare isHls: boolean | undefined
  declare isBlocked: boolean | undefined
  declare blockedQualityLevels: any | undefined

  private mouseDownTarget: EventTarget | null = null
  private mouseDownOutsideChildren = false

  constructor(tag, options, ready) {
    super(tag, options, ready)

    // Base player constructor calls resetCache_ which sets volume to 1, update it with real value asap
    setCacheLastVolume(this as unknown as TPlayerBase)

    // manually add classes from options
    if (options.className) {
      options.className.split(' ').forEach((c) => this.addClass(c))
    }

    // Video.js wires user-activity tracking only on first 'play'; if content errors
    // before play, tracking never starts. Init eagerly and no-op the play-time hook.
    ;(this as any).listenForUserActivity_()
    ;(this as any).listenForUserActivity_ = () => {}

    this.el_.addEventListener('mousedown', this.handleMouseDown.bind(this))
    this.el_.addEventListener('mouseup', this.handleMouseUp.bind(this))

    BaseButton.prototype.handleKeyDown = this.modifiedHandleKeyDown.bind(this) // FP-3291 SpaceBar while button in focus
  }

  modifiedHandleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      // Button stops Space Bar keys, overriding https://github.com/videojs/video.js/blob/09eb7150453bb2cbd96e638be3e115590ae98578/src/js/button.js#L113-L128
      // Even if Button passes event, Clickable Component calls 'click' instead of 'keyDown', that also incorrect.
      return e.stopPropagation()
    }

    this.handleKeyDown(e)
  }

  override handleKeyDown(e: KeyboardEvent) {
    if (e.code === 'KeyM') {
      if (
        this.hasClass('vjs-controls-disabled') ||
        this.hasClass('vjs-error')
      ) {
        e.stopPropagation()
        return
      }
    }

    super.handleKeyDown(e)
  }

  handleMouseDown(e: Event) {
    // Ignore togglePause if there're any menus or children
    if (
      this.hasClass('vjs-menu-opened') ||
      (this.hasClass('vjs-children') && this.isTargetInsideChildrenSlot(e))
    ) {
      this.mouseDownTarget = null
      this.mouseDownOutsideChildren = false
      return
    }

    // Ignore if mousedown started on control buttons
    if (e.target instanceof HTMLElement && e.target.closest('.vjs-control')) {
      this.mouseDownTarget = null
      this.mouseDownOutsideChildren = false
      return
    }

    // Ignore taps from touch devices (tap handler is in tap-handler.ts)
    if (isTouchDevice()) {
      this.mouseDownTarget = null
      this.mouseDownOutsideChildren = false
      return
    }

    // Store where mousedown occurred
    this.mouseDownTarget = e.target
    this.mouseDownOutsideChildren = this.hasClass('vjs-children')
  }

  handleMouseUp(e: Event) {
    // Only process if mousedown happened on valid target
    if (!this.mouseDownTarget) {
      return
    }

    // Reset the stored target
    this.mouseDownTarget = null
    const ignoreChildren = this.mouseDownOutsideChildren
    this.mouseDownOutsideChildren = false

    // Ignore if mouseup happened on control buttons
    if (e.target instanceof HTMLElement && e.target.closest('.vjs-control')) {
      return
    }

    // Ignore if mouseup happened inside rendered children
    if (this.isTargetInsideChildrenSlot(e)) {
      return
    }

    // Ignore taps from touch devices (tap handler is in tap-handler.ts)
    if (isTouchDevice()) {
      return
    }

    // Only toggle pause if both mousedown and mouseup happened on valid areas
    e.stopPropagation()
    e.preventDefault()
    this.togglePause(undefined, { ignoreChildren })
  }

  isTargetInsideChildrenSlot(e: Event) {
    const target = e.target
    const slot = this.getChild('slot') as ChildrenSlot | undefined
    const slotEl = slot?.el()

    return Boolean(slotEl && target instanceof Node && slotEl.contains(target))
  }

  override handleHotkeys(e: KeyboardEvent) {
    if (this.hasClass('vjs-controls-disabled')) {
      e.stopPropagation()
      e.preventDefault()
      return
    }

    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      e.stopPropagation()
      e.preventDefault()
      VolumePanel.prototype.handleArrows.call(this, e)
    }

    if (e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
      e.stopPropagation()
      e.preventDefault()
      if (!this.hasClass('vjs-ad-playing')) return
      this.arrowSeekHandlers?.holdCountdown(e)
    }

    super.handleHotkeys(e)
  }

  disabledTogglePause(options?: { ignoreChildren?: boolean }) {
    return (
      this.hasClass('vjs-controls-disabled') ||
      (this.hasClass('vjs-ad-playing') && !this.paused()) ||
      this.hasClass('vjs-error') ||
      (!options?.ignoreChildren && this.hasClass('vjs-children')) ||
      (this.hasClass('vjs-live-progress-bar') &&
        !this.hasClass('vjs-rewindable-progress-bar'))
    )
  }

  togglePause(event?: Event, options?: { ignoreChildren?: boolean }) {
    const ignoreChildren =
      options?.ignoreChildren ??
      Boolean(
        event &&
        this.hasClass('vjs-children') &&
        !this.isTargetInsideChildrenSlot(event)
      )

    if (this.disabledTogglePause({ ignoreChildren })) {
      return
    }

    this.player_.trigger(Events.User.Intent, Intent.TogglePause)
    const isPaused = this.paused()

    if (isPaused) silencePromise(this.play())
    else this.pause()

    this.playPauseAnimation?.animate(isPaused)
  }

  setNewSource(source) {
    this.src(source)
    const isAnyAppleDevice =
      videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS
    const tech = this.player_.tech_

    // FP-3599 This 'false' allows drm content to play
    if (isAnyAppleDevice) {
      if (isDash(source) && tech) {
        tech.featuresNativeTextTracks = false
      }
    }

    // use _native_ audio tracks for HLS sources on Safari and iOS
    // this will override the default `nativeAudioTracks: false` option
    // see https://setplexapps.atlassian.net/browse/FP-3032
    if (isAnyAppleDevice) {
      const isHlsSource = isHls(source)
      tech?.overrideNativeAudioTracks?.(!isHlsSource)
    }
  }

  resetSoft() {
    /* The easiest method to remove old source state from the screen instantly.
     * Player restores tech when new source provided
     */

    if (this.player_.tech_) {
      this.player_.techName_ = null
      this.player_.loadTech_('Html5')
      this.player_.trigger('playerreset')
    }
  }

  // listener to handle new sources
  @on(Events.Media.Load)
  onNewSource(source: Events.Media.Load) {
    this.cachedSource = source
    this.isHls = source?.type === 'application/x-mpegURL'

    // Reset has-started state as new source not started yet.
    this.hasStarted(false)

    if (source) {
      // Reset blocked by limitations status
      this.isBlocked = false

      this.setNewSource(source)

      if (source.src) {
        // Restart inactivity timer when playback source is ready (cold open / switch).
        this.reportUserActivity(undefined)
      }
    } else {
      this.resetSoft()
    }
  }

  @on(Events.Media.IsRenderedChildren)
  handleChildrenState(isRenderedChildren: boolean) {
    if (isRenderedChildren) {
      this.addClass('vjs-children')
      // Seed activity so the 3s inactivity timeout arms on cold-open overlays.
      // Avoid userActive(false) — that hid controls too early and blocked reopen.
      this.reportUserActivity(undefined)
      return
    }

    this.removeClass('vjs-children')
    this.clearStuckActivityInterval()
  }

  @on(Events.Media.ContentChanged)
  onContentChanged() {
    // Sync before React IsRenderedChildren catches up (two-phase update).
    const slotEl = (this.getChild('slot') as ChildrenSlot | undefined)?.el()
    const hasChildren = (slotEl?.children.length ?? 0) > 0
    this.toggleClass('vjs-children', hasChildren)
    if (hasChildren) return

    this.clearStuckActivityInterval()
    // userActive(true) is a no-op when already active; reportUserActivity restarts the timer.
    this.reportUserActivity(undefined)
  }

  /**
   * Episode List (and other overlays rendered into the children slot) stops
   * propagation of `mouseup` so it never reaches the player element. Video.js's
   * own `listenForUserActivity_` starts a `mouseInProgress` interval on
   * `mousedown` and only clears it on `mouseup`/`mouseleave` — when that
   * `mouseup` is swallowed, the interval runs forever and keeps reporting
   * activity, so controls never hide (FP-3858). Re-dispatch `mouseup` through
   * the player's own event bus (not the DOM) to run Video.js's internal
   * handler and clear the interval, without affecting our own raw
   * `handleMouseUp` DOM listener or triggering `togglePause`.
   */
  clearStuckActivityInterval() {
    this.player_.trigger('mouseup')
  }

  @on(Events.Media.ProgressBarChanged)
  handleProgressBarClass(progressBar: Events.Media.ProgressBarChanged) {
    let isInteractive = progressBar ? (progressBar[4] ?? true) : false

    // means seeking (also seeking with rewind api) is allowed
    if (progressBar === undefined || isInteractive)
      this.addClass('vjs-rewindable-progress-bar')
    else this.removeClass('vjs-rewindable-progress-bar')

    if (progressBar !== undefined) this.addClass('vjs-live-progress-bar')
    else this.removeClass('vjs-live-progress-bar')
  }

  @on(Events.Media.IsUrlPending)
  handleUrlPending(isUrlPending: boolean) {
    this.toggleClass('vjs-url-pending', Boolean(isUrlPending))
  }

  @on(Events.Media.AdStateChanged)
  handleAdStateChange(adState: AdState) {
    this.toggleClass('vjs-ad-playing', Boolean(adState?.isAdPlaying))
  }

  @on(Events.Media.IsPlayerDisabled)
  handlePlayerDisabled(isPlayerDisabled: boolean) {
    this.toggleClass('vjs-x-disable-player', Boolean(isPlayerDisabled))
    this.toggleClass('vjs-controls-disabled', Boolean(isPlayerDisabled))
  }

  @on('seeking')
  onSeeking() {
    // FP-3499
    if (
      Math.floor(this.player_.currentTime()) ===
      Math.floor(this.player_.duration())
    )
      this.player_.trigger('ended')
  }

  setBlockedQualityLevels(qualityLevels) {
    // Clone and store the quality levels
    if (qualityLevels.levels_.length) {
      this.blockedQualityLevels = {
        ...qualityLevels,
        levels_: [...qualityLevels.levels_],
        length: qualityLevels.length,
        selectedIndex: qualityLevels.selectedIndex,
      }
    }
  }
}

Player.options = {
  children: [
    // `MediaLoader` decides which playback technology to load when a player is initialized, required to play anything
    BaseMediaLoader.as(),

    // handle keyboard shortcuts for the player
    Hotkeys,

    // debug overlay with stream and player info
    DebugOverlay,

    // clicable component that handles showing the poster image for the player
    BasePosterImage.as(),

    // component for displaying text track cues, required for displaying subtitles
    BaseTextTrackDisplay.as(),

    // checking live current time and determining when the player is at or behind the live edge
    LiveTracker.as('liveTracker'),

    // handle double-tap to seek on touch devices
    TapHandler,

    // updater for current time to synchronize counters
    CurrentTimeUpdater,

    // display that indicates an error has occurred, this means that the video is unplayable
    ErrorDisplay.as('errorDisplay'),

    // empty slot for children elements rendering
    // add this component as property `.slot` to the player instance
    ChildrenSlot.as('slot'),

    // Poster image component
    Poster,

    // There is no requirements and design yet
    // modal dialog for manipulating text tracks (subtitles) settings
    // TextTrackSettings.as('textTrackSettings'),

    // component to track size of the player, triggers `playerresize` event on player
    BaseResizeManager.as(),

    // disable ad detection for now
    // AdDetection,

    // chromecast message
    ChromeCastingMessage,

    // loading spinner for use during waiting/loading events
    LoadingSpinner,

    // Animation of seeking when user double taps
    TapSeekAnimation,

    // Animation of seeking with arrow keys
    ArrowSeekAnimation,
  ],
}
